import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlUserSettingsDigitalSignatures } from '../../Constants';
import { IAppReduxStore, IAction, SlicePropsWithData } from '../store';
import { filesReducers, IFileInfoState, initialFileState } from '../general/files';

/**
 * Initial slice state value.
 */
const initialState: IProjectUserSignatureSettingsState = {
    data: null,
    isChangeCredentialsDialogOpen: false,
    isSignatureSaved: false,
    areCredentialsSaved: false,
    loading: false,
    error: null,
    signature: null,
    ...initialFileState,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    openChangeCredentialsDialog(state: IProjectUserSignatureSettingsState) {
        state.isChangeCredentialsDialogOpen = true;
    },
    closeChangeCredentialsDialog(state: IProjectUserSignatureSettingsState) {
        state.isChangeCredentialsDialogOpen = false;
        state.isSignatureSaved = false;
    },

    resetAreSignatureCredentialsSaved(state: IProjectUserSignatureSettingsState) {
        state.areCredentialsSaved = false;
    },

    resetIsSignatureSaved(state: IProjectUserSignatureSettingsState) {
        state.isSignatureSaved = false;
    },

    resetUserSignatureError(state: IProjectUserSignatureSettingsState) {
        state.error = null;
    },

    ...filesReducers,
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches project signature settings  from the backend.
     */
    getProjectSignatureSettings: async ({
        replicaId,
    }: {
        replicaId: string;
    }): Promise<IProjectUserSignature> => {
        try {
            const requestConfig: RequestInit = {
                method: 'GET',
                headers: {
                    replicaId: replicaId,
                },
            };

            const response = await fetch(urlUserSettingsDigitalSignatures, requestConfig);

            return response.ok ? response.json() : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    saveProjectSignatureSettings: async (
        {
            properties,
            replicaId,
            typeOfChange,
        }: {
            properties: { [key: string]: SignatureSettingValue };
            typeOfChange: UserSignatureChangeTypeEnum;
            replicaId: string;
        },
        thunkApi,
    ): Promise<{
        properties: { [key: string]: SignatureSettingValue };
        typeOfChange: UserSignatureChangeTypeEnum;
    }> => {
        const { signature } = (thunkApi.getState() as IAppReduxStore).projectUserSignatureSettings;

        const dataToSave = {
            ...signature,
            ...properties,
        };

        try {
            const data: FormData = new FormData();

            data.append(
                'settings',
                JSON.stringify({
                    ...dataToSave,
                }),
            );

            const response = await fetch(`${urlUserSettingsDigitalSignatures}`, {
                method: 'POST',
                headers: {
                    replicaId: replicaId,
                },
                body: data,
            });
            return response.ok
                ? Promise.resolve({ properties, typeOfChange })
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    saveProjectSignatureFile: async (
        { file, replicaId }: { file: File; replicaId: string },
        thunkApi,
    ): Promise<{
        properties: { [key: string]: SignatureSettingValue };
    }> => {
        const { aes, qes, signmeUsername, signmePAK } = (thunkApi.getState() as IAppReduxStore)
            .projectUserSignatureSettings.signature;

        const dataToSave = {
            aes,
            qes,
            signmeUsername,
            signmePAK,
        };

        try {
            const data: FormData = new FormData();

            data.append(
                'settings',
                JSON.stringify({
                    ...dataToSave,
                }),
            );

            data.append('filePfx', file);

            const response = await fetch(`${urlUserSettingsDigitalSignatures}`, {
                method: 'POST',
                headers: {
                    replicaId: replicaId,
                },
                body: data,
            });
            return response.ok
                ? Promise.resolve({
                      properties: { aes, qes, signmeUsername, signmePAK, pfxFile: file.name },
                  })
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectUserSignatureSettingsState>) => {
        /**
         * Reducers for the 'getProjectSignatureSettings' thunk.
         */
        builder.addCase(getProjectSignatureSettings.pending, state => {
            state.loading = true;
        });
        builder.addCase(getProjectSignatureSettings.fulfilled, (state, action) => {
            state.signature = action.payload;
            state.loading = false;
        });
        builder.addCase(
            getProjectSignatureSettings.rejected,
            (state, action: IAction<SerializedError>) => {
                state.error = action.error;
                state.loading = false;
            },
        );

        /**
         * Reducers for the 'saveProjectSignatureSettings' thunk.
         */
        builder.addCase(
            saveProjectSignatureSettings.pending,
            (state: IProjectUserSignatureSettingsState) => {
                state.loading = true;
                state.isSignatureSaved = false;
                state.areCredentialsSaved = false;
            },
        );

        builder.addCase(saveProjectSignatureSettings.fulfilled, (state, action) => {
            if (action.payload.typeOfChange === UserSignatureChangeTypeEnum.Credentials) {
                state.areCredentialsSaved = true;
            } else {
                state.isSignatureSaved = true;
            }

            state.signature = { ...state.signature, ...action.payload.properties };

            state.loading = false;
            state.error = null;
            state.fileInfo = null;
        });
        builder.addCase(
            saveProjectSignatureSettings.rejected,
            (state: IProjectUserSignatureSettingsState, action: IAction<SerializedError>) => {
                state.error = action.error;
                state.loading = false;
                state.isSignatureSaved = false;
                state.areCredentialsSaved = false;
            },
        );

        /**
         * Reducers for the 'saveProjectSignatureFile' thunk.
         */
        builder.addCase(
            saveProjectSignatureFile.pending,
            (state: IProjectUserSignatureSettingsState) => {
                state.loading = true;
                state.isSignatureSaved = false;
            },
        );

        builder.addCase(saveProjectSignatureFile.fulfilled, (state, action) => {
            state.isSignatureSaved = true;

            state.signature = { ...state.signature, ...action.payload.properties };

            state.loading = false;
            state.error = null;
            state.fileInfo = null;
        });
        builder.addCase(
            saveProjectSignatureFile.rejected,
            (state: IProjectUserSignatureSettingsState, action: IAction<SerializedError>) => {
                state.error = action.error;
                state.loading = false;
                state.isSignatureSaved = false;
            },
        );
    },
};

export enum AesTypeEnum {
    Pfx = 'pfx',
    Signme = 'signme',
}

export enum QesTypeEnum {
    Pfx = 'pfx',
    Signme = 'signme',
    Card = 'card',
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectUserSignatureSettingsState
    extends SlicePropsWithData<IProjectUserSignature>,
        IFileInfoState {
    isChangeCredentialsDialogOpen: boolean;
    isSignatureSaved: boolean;
    areCredentialsSaved: boolean;
    signature: IProjectUserSignature;
}

/**
 * Interface that describes the signature settings for a user.
 */

export interface IProjectUserSignature {
    aes: AesTypeEnum;
    qes: QesTypeEnum;
    signmeUsername: string;
    signmePAK: string;
    pfxFile: string;
}

export interface IProjectUserSignatureChangeType {
    credentials: string;
    qes: QesTypeEnum;
    signmeUsername: string;
    signmePAK: string;
    pfxFile: string;
}

export enum UserSignatureChangeTypeEnum {
    Credentials = 'credentials',
    MainSettings = 'mainSettings',
}

type SignatureSettingValue = string | AesTypeEnum | QesTypeEnum;

/**
 * Expose thunk functions as exports.
 */
export const getProjectSignatureSettings = createAsyncThunk(
    `${urlUserSettingsDigitalSignatures}-get`,
    thunks.getProjectSignatureSettings,
);
export const saveProjectSignatureSettings = createAsyncThunk(
    `${urlUserSettingsDigitalSignatures}-post`,
    thunks.saveProjectSignatureSettings,
);

export const saveProjectSignatureFile = createAsyncThunk(
    `${urlUserSettingsDigitalSignatures}-post-file`,
    thunks.saveProjectSignatureFile,
);

/**
 * Create the redux slice.
 */
export const projectUserSignatureSettingsSlice = createSlice({
    name: 'projectUserSignatureSettings',
    initialState: initialState,
    reducers,
    extraReducers: thunks._reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    openChangeCredentialsDialog,
    closeChangeCredentialsDialog,
    resetAreSignatureCredentialsSaved,
    resetIsSignatureSaved,
    resetUserSignatureError,
} = projectUserSignatureSettingsSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectUserSignatureSettingsSlice.reducer;
